import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { fromEvent } from "rxjs";
import { debounceTime, take } from "rxjs/operators";
import { UserSessionService } from '../../shared/service/user-session.service';

export class FormUtils {

  public static enable(...args: UntypedFormControl[]) {
    for (const control of args) {
      control.enable({onlySelf: true});
    }
  }

  public static disable(...controls: UntypedFormControl[]) {
    for (const control of controls) {
      control.disable({onlySelf: true});
    }
  }

  /**
   * recursively goes through a form group and marks every control as touched.
   */
  public static markFormAsTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls)
      .forEach(key => {
        const formGroupMember = formGroup.controls[key];
        return FormUtils.touchMemberBasedOnType(formGroupMember);
      });
  }

  public static updateValidity(formGroup: UntypedFormGroup): void{
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.controls[key] as UntypedFormControl;
    
      control.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    });
  }

  /**
   * This method is used to set the drop box value for the country field.
   * @param obj1 is object to compare
   * @param obj2 is other object to compare
   */
  public static compareValueToObject(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

   public static navigateTo(url: string): void {
    window.location.href = url;
  }

  private static touchMemberBasedOnType(formGroupMember) {
    if (formGroupMember instanceof UntypedFormControl) {
      formGroupMember.markAsTouched();
    } else if (formGroupMember instanceof UntypedFormGroup) {
      FormUtils.markFormAsTouched(formGroupMember);
    } else if (formGroupMember instanceof UntypedFormArray) {
      this.markArrayAsTouched(formGroupMember);
    } else {
      throw Error('unexpected variable inside form group');
    }
  }

  private static markArrayAsTouched(formArray: UntypedFormArray) {
    formArray.controls.forEach(control => {
        if (control instanceof UntypedFormControl) {
          control.markAsTouched();
        } else if (control instanceof UntypedFormGroup) {
          FormUtils.markFormAsTouched(control);
        } else {
          throw Error('unexpected variable inside form array');
        }
      }
    );
  }


  /**
   * Marks all controls in a form group as touched and dirty
   * @param formGroup to be rehydrate
   */
  public static markControlsAsTouchedAndDirty(formGroup: AbstractControl): void {
    if (formGroup instanceof UntypedFormGroup) {
      Object.keys(formGroup.controls)
        .map(key => {
          if (!!formGroup.controls[key].value) {
            formGroup.controls[key].markAsTouched();
            formGroup.controls[key].markAsDirty();
          }

          if (formGroup.controls[key] instanceof UntypedFormGroup) {
            this.markControlsAsTouchedAndDirty(formGroup.controls[key]);
          }
        });
    }
  }

  public static findFirstInvalidControl(someForm: UntypedFormGroup){
    let firstInvalidControl: string = null;
    Object.keys(someForm.controls).forEach(controlName => {
      if(!someForm.get(controlName).valid){
        if(firstInvalidControl === null){
          firstInvalidControl = controlName;
        }
      }
    });

    return firstInvalidControl;
  }

  public static scrollToFirstInvalidControl(firstInvalidControl: HTMLElement) {

    window.scroll({
      top: this.getTopOffset(firstInvalidControl),
      left: 0,
      behavior: "smooth"
    });

    fromEvent(window, "scroll")
      .pipe(
        debounceTime(100),
        take(1)
      )
      .subscribe(() => firstInvalidControl.focus());
  }

  private static getTopOffset(controlEl: HTMLElement): number {
    const labelOffset = 60;
    return controlEl.getBoundingClientRect().top + window.scrollY - labelOffset;
  }

  public static _validate(form: UntypedFormGroup): void {
    form.markAllAsTouched();

    Object.keys(form.controls).forEach(key => {
      const control = form.controls[key] as UntypedFormControl;

      control.updateValueAndValidity({onlySelf: false, emitEvent: true});
    });
  }

  public static capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  public static removeHiddenChar(str : String){
    return str.replace(/[\u{0080}-\u{FFFF}]/gu, "");
  }

  //Sometimes Drupal insert HTML tags to content that is not supposed to have any tags in it
  //This function clears out all HTML tags that might have been inserted into the Drupal content
  public static sanitize(str : String){
    return str.replace(/<[^>]*>/g, '');
  }

  public static padWithZero(value: string | number): string {
    if (typeof value === 'number') {
      value = value.toString();
    }
    return value.length < 2 ? '0' + value : value;
  }

  public static stringToBoolean(str): boolean | "error" {
    const lowerStr = str.toLowerCase();
    if (lowerStr === 'true') {
      return true;
    } else if (lowerStr === 'false') {
      return false;
    }
    return "error";
  } 
}